import React, { useMemo } from "react";
import Image from "next/image";
import { CliCards, CliSubTitle, CliTitle } from "./product";
import { cliCardImage } from "../images";

type Props = object;

const cliCardData = [
  {
    ...CliCards[0],
    image: cliCardImage.promptToCommand,
    isLarge: true,
  },
  {
    ...CliCards[1],
    image: cliCardImage.availableAnywhere,
    isLarge: false,
  },
  {
    ...CliCards[2],
    image: cliCardImage.codebaseContext,
    isLarge: false,
  },
];

const CliCard = ({
  title,
  subTitle,
  image,
  isLarge,
}: {
  title: string;
  subTitle: string;
  image: string;
  isLarge: boolean;
}) => (
  <article
    className={`rounded-2xl bg-zinc-100 p-3 md:p-4 ${isLarge ? "mx-auto w-full max-w-[720px]" : ""}`}
  >
    <div className="overflow-hidden rounded-xl">
      <Image
        src={image}
        alt={title}
        width={1280}
        height={900}
        className="h-auto w-full rounded-xl object-cover"
        priority={isLarge}
      />
    </div>
    <div className="px-1 py-5 md:px-2">
      <h3 className="font-dm mb-2 text-[18px] font-medium tracking-[-0.03em] md:text-[20px]">
        {title}
      </h3>
      <p className="font-dm text-[14px] leading-[1.6] tracking-[-0.02em] text-zinc-700 md:text-[15px]">
        {subTitle}
      </p>
    </div>
  </article>
);

function Cli({}: Props) {
  // Memoize the card separation to prevent recreation on every render
  const { largeCard, smallCards } = useMemo(() => {
    const [large, ...small] = cliCardData;
    return { largeCard: large, smallCards: small };
  }, []);

  return (
    <section id="cli" className="rounded-t-[40px] bg-[#FFFFFF]">
      <div className="mx-auto w-full max-w-6xl px-4 py-16 text-[#0B0B0B] md:py-24">
        <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-10">
          <div>
            {/* Pill (light theme) */}
            <div className="mb-4 inline-flex items-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#E6E6E6] bg-[#F7F7F7] px-3 py-1 text-[12px] leading-[20px] tracking-[-0.03em] text-[#4A4A4A] md:px-3.5 md:py-1.5 md:text-[13px]">
                <span className="h-[10px] w-[10px] rounded-full border border-[#D9D9D9] bg-[#EDEDED]" />
                Terminal
              </div>
            </div>

            <h2 className="font-dm text-[34px] leading-[1.08] tracking-[-0.05em] text-[#0B0B0B] sm:text-[40px] md:text-[52px]">
              {CliTitle}
            </h2>
            <p className="font-dm mt-4 max-w-[560px] text-[15px] leading-[1.6] tracking-[-0.03em] text-zinc-700 sm:text-[16px] md:text-[18px]">
              {CliSubTitle}
            </p>

            <div className="mt-6">
              <a
                href="https://npmjs.com/package/pasoma"
                target="_blank"
                className="inline-flex items-center rounded-full bg-[#0B0B0B] px-5 py-2.5 text-[14px] font-medium tracking-[-0.02em] text-white shadow-sm hover:opacity-90 md:px-6 md:py-3 md:text-[15px]"
              >
                Install Pasoma CLI
              </a>
            </div>
          </div>

          <CliCard {...largeCard} />
        </div>

        <div className="mx-auto mt-10 grid max-w-6xl grid-cols-1 gap-6 md:mt-14 md:grid-cols-2">
          {smallCards.map((card, index) => (
            <CliCard key={index} {...card} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default Cli;
